import { apiRequest } from './apiRequest'
import {
    cacheResolvedTenantId,
    getOptionalTenantId,
    getTenantId as getResolvedTenantId,
    resolveTenantId as resolveTenantIdAsync
} from '../utils/tenantUtils'
import { getApiBaseUrl } from '../lib/apiBaseUrl'

export interface LoginRequest {
    tenantId: string
    username: string
    password: string
    rememberMe?: boolean
    deviceId?: string
    deviceInfo?: {
        deviceType: string
        browser: string
        os: string
        location?: string
    }
}

export interface LoginResponse {
    success: boolean
    message: string
    data?: {
        user: {
            id: string
            username: string
            firstName: string
            lastName: string
            mobile: string
            role: string
            status: string
            isMobileVerified: boolean
            tenantId: string
            lastLogin: string
        }
        tokens: {
            accessToken: string
            refreshToken: string
            expiresIn: number
        }
        deviceId: string
    }
    error?: string
}

export interface User {
    id: string
    username: string
    firstName: string
    lastName: string
    mobile: string
    role: string
    status: string
    isMobileVerified: boolean
    tenantId: string
    lastLogin: string
}

export interface AuthTokens {
    accessToken: string
    refreshToken: string
    expiresIn: number
}

interface BasicApiResponse {
    success: boolean
    message?: string
    data?: any
}

class AuthService {
    private readonly baseURL = getApiBaseUrl()
    
    /**
     * Get tenant ID from multiple sources (private helper)
     */
    private getTenantIdInternal(): string {
        return getResolvedTenantId(this.getCurrentUser() || undefined)
    }

    /**
     * Login user
     */
    async login(loginData: LoginRequest): Promise<LoginResponse> {
        try {
            console.log('🔐 Login request:', {
                username: loginData.username,
                tenantId: loginData.tenantId,
                rememberMe: loginData.rememberMe
            })

            const response = await apiRequest<LoginResponse>('/api/v1/user-auth/login', {
                method: 'POST',
                body: {
                    tenantId: loginData.tenantId || this.getTenantIdInternal(),
                    username: loginData.username,
                    password: loginData.password,
                    deviceId: loginData.deviceId || this.generateDeviceId(),
                    deviceInfo: loginData.deviceInfo || this.getDeviceInfo()
                }
            })

            if (response.success && response.data) {
                // Store tokens in localStorage
                localStorage.setItem('accessToken', response.data.tokens.accessToken)
                localStorage.setItem('refreshToken', response.data.tokens.refreshToken)
                localStorage.setItem('deviceId', response.data.deviceId)
                localStorage.setItem('tenantId', response.data.user.tenantId)
                cacheResolvedTenantId(response.data.user.tenantId)

                // Store user info
                localStorage.setItem('user', JSON.stringify(response.data.user))

                // Store remember me preference
                if (loginData.rememberMe) {
                    localStorage.setItem('rememberMe', 'true')
                } else {
                    localStorage.removeItem('rememberMe')
                }

                console.log('✅ Login successful:', {
                    userId: response.data.user.id,
                    username: response.data.user.username,
                    role: response.data.user.role
                })
            }

            return response
        } catch (error) {
            console.error('❌ Login error:', error)
            throw error
        }
    }

    /**
     * Force logout from all devices (admin function)
     */
    async forceLogoutFromAllDevices(tenantId: string, _username: string): Promise<void> {
        try {
            // For demo purposes, we'll use the known user ID
            // In production, you would first get the user ID by username
            const userId = '688f88606eda3f13dcdfca98' // kadaejung1 user ID

            const response = await apiRequest<BasicApiResponse>(`/api/v1/user-auth/admin/force-logout/${userId}`, {
                method: 'POST',
                body: {
                    tenantId: tenantId,
                    reason: 'user_requested_force_logout'
                }
            })

            if (response.success) {
                console.log('✅ Force logout successful:', response.data)
            } else {
                throw new Error(response.message || 'Force logout failed')
            }
        } catch (error) {
            console.error('❌ Force logout error:', error)
            throw error
        }
    }

    /**
     * Logout user
     */
    async logout(): Promise<void> {
        try {
            const refreshToken = localStorage.getItem('refreshToken')
            const deviceId = localStorage.getItem('deviceId')

            if (refreshToken && deviceId) {
                // Call logout API to invalidate refresh token
                await apiRequest('/api/v1/user-auth/logout', {
                    method: 'POST',
                    body: {
                        refreshToken,
                        deviceId
                    }
                })
            }
        } catch (error) {
            console.warn('⚠️ Logout API call failed:', error)
        } finally {
            // Clear local storage regardless of API call result
            localStorage.removeItem('accessToken')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('deviceId')
            localStorage.removeItem('user')
            localStorage.removeItem('rememberMe')

            console.log('✅ Logout completed')
        }
    }

    /**
     * Get current user from localStorage
     */
    getCurrentUser(): User | null {
        try {
            const userStr = localStorage.getItem('user')
            return userStr ? JSON.parse(userStr) : null
        } catch (error) {
            console.error('❌ Error parsing user from localStorage:', error)
            return null
        }
    }

    /**
     * Get access token from localStorage
     */
    getAccessToken(): string | null {
        return localStorage.getItem('accessToken')
    }

    /**
     * Get access token (alias for getAccessToken)
     */
    getToken(): string | null {
        return this.getAccessToken()
    }

    /**
     * Parse viewAsToken from current URL hash (e.g. #/dashboard?viewAsToken=xxx).
     * Used when admin opens View As to user-classic (port 5174).
     */
    getViewAsTokenFromHash(): string | null {
        if (typeof window === 'undefined') return null
        const hash = window.location.hash
        const qIndex = hash.indexOf('?')
        if (qIndex === -1) return null
        const params = new URLSearchParams(hash.slice(qIndex))
        return params.get('viewAsToken')
    }

    /**
     * Apply view-as token from admin: store token, fetch profile, store user.
     * Call when URL hash contains viewAsToken. Does not touch refresh token.
     */
    async applyViewAsToken(viewAsToken: string): Promise<{ ok: boolean; error?: string }> {
        const profileUrl = `${this.baseURL}/api/v1/user-auth/profile`
        try {
            localStorage.setItem('accessToken', viewAsToken)
            const tenantId = getOptionalTenantId(this.getCurrentUser() || undefined) || ''
            const res = await fetch(profileUrl, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${viewAsToken}`,
                    'Content-Type': 'application/json',
                    ...(tenantId && { 'X-Tenant-ID': tenantId }),
                },
                credentials: 'include',
            })
            const data = (await res.json().catch(() => ({}))) as { success?: boolean; data?: { user?: unknown }; message?: string }
            if (!res.ok || !data?.success || !data?.data?.user) {
                localStorage.removeItem('accessToken')
                return { ok: false, error: (data as any)?.message || 'โหลดข้อมูลผู้ใช้ไม่สำเร็จ' }
            }
            const user = data.data.user as User
            localStorage.setItem('user', JSON.stringify(user))
            localStorage.setItem('tenantId', user.tenantId || '')
            if (user.tenantId) {
                cacheResolvedTenantId(user.tenantId)
            }
            return { ok: true }
        } catch (e) {
            localStorage.removeItem('accessToken')
            return { ok: false, error: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด' }
        }
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        const token = this.getAccessToken()
        const user = this.getCurrentUser()
        return !!(token && user)
    }

    /**
     * Get tenant ID (public method)
     */
    getTenantId(): string {
        return this.getTenantIdInternal()
    }

    /**
     * Resolve tenant ID using runtime/domain mapping sources
     */
    async resolveTenantId(): Promise<string> {
        return resolveTenantIdAsync(this.getCurrentUser() || undefined)
    }

    /**
     * Refresh access token
     */
    async refreshToken(): Promise<{ success: boolean; data?: { token: string } }> {
        try {
            const refreshToken = localStorage.getItem('refreshToken')

            if (!refreshToken) {
                return { success: false }
            }

            const response = await apiRequest<BasicApiResponse>('/api/v1/user-auth/refresh', {
                method: 'POST',
                body: {
                    refreshToken: refreshToken
                }
            })

            if (response.success && response.data) {
                // Handle both response formats: { tokens: {accessToken, refreshToken} } or { accessToken, refreshToken }
                const tokens = response.data.tokens || response.data

                // Update stored tokens
                localStorage.setItem('accessToken', tokens.accessToken)
                localStorage.setItem('refreshToken', tokens.refreshToken)

                return {
                    success: true,
                    data: {
                        token: tokens.accessToken
                    }
                }
            }

            return { success: false }
        } catch (error) {
            console.error('❌ Refresh token error:', error)
            return { success: false }
        }
    }

    /**
     * Change password for the logged-in user
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
        const user = this.getCurrentUser()
        const token = this.getAccessToken()
        const tenantId = this.getTenantIdInternal()

        if (!user?.id || !token || !tenantId) {
            return { success: false, error: 'ไม่พบข้อมูลผู้ใช้' }
        }

        try {
            const response = await fetch(`${this.baseURL}/api/v1/user/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    'X-Tenant-ID': tenantId,
                },
                credentials: 'include',
                body: JSON.stringify({ currentPassword, newPassword }),
            })

            const data = (await response.json().catch(() => ({}))) as { success?: boolean; message?: string }
            if (!response.ok) {
                return {
                    success: false,
                    error: data?.message || (response.status === 400 ? 'รหัสผ่านเดิมไม่ถูกต้อง' : 'เปลี่ยนรหัสผ่านไม่สำเร็จ')
                }
            }

            return { success: true }
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'เกิดข้อผิดพลาด'
            }
        }
    }

    /**
     * Generate device ID
     */
    private generateDeviceId(): string {
        let deviceId = localStorage.getItem('deviceId')
        if (!deviceId) {
            deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
            localStorage.setItem('deviceId', deviceId)
        }
        return deviceId
    }

    /**
     * Get device information
     */
    private getDeviceInfo() {
        const userAgent = navigator.userAgent
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)

        return {
            deviceType: isMobile ? 'mobile' : 'desktop',
            browser: this.getBrowserName(userAgent),
            os: this.getOSName(userAgent),
            location: 'unknown' // Could be enhanced with geolocation API
        }
    }

    /**
     * Get browser name from user agent
     */
    private getBrowserName(userAgent: string): string {
        if (userAgent.includes('Chrome')) return 'Chrome'
        if (userAgent.includes('Firefox')) return 'Firefox'
        if (userAgent.includes('Safari')) return 'Safari'
        if (userAgent.includes('Edge')) return 'Edge'
        return 'Unknown'
    }

    /**
     * Get OS name from user agent
     */
    private getOSName(userAgent: string): string {
        if (userAgent.includes('Windows')) return 'Windows'
        if (userAgent.includes('Mac')) return 'macOS'
        if (userAgent.includes('Linux')) return 'Linux'
        if (userAgent.includes('Android')) return 'Android'
        if (userAgent.includes('iOS')) return 'iOS'
        return 'Unknown'
    }
}

export const authService = new AuthService()
export default authService